import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { filter, of, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { bypassAllowed } from './auth-bypass';

/**
 * Lets the navigation through once AuthService has settled the session.
 *
 * MsalGuard alone ends on the outcome of handleRedirectPromise, which MSAL
 * memoises: after one refused redirect it rejects every guarded navigation
 * until the page reloads, even once a token is in hand. It therefore only
 * runs when no account exists, to start the sign-in. While the sign-in has
 * failed the navigation stays pending, the address bar keeps the requested
 * page and the retry screen sends the user back there.
 */
export const authGuard: CanActivateFn = (route, state) => {
  if (bypassAllowed()) return true;
  const msalGuard = inject(MsalGuard);
  return toObservable(inject(AuthService).sessionStatus).pipe(
    filter(status => status === 'signedIn' || status === 'signedOut'),
    take(1),
    switchMap(status => (status === 'signedIn' ? of(true) : msalGuard.canActivate(route, state))),
  );
};
